import type { ApiResponse, Item } from '@packages/shared-types';

import { apiClient } from '@/services/apiClient';

export type CreateItemPayload = {
  name: string;
  sku?: string;
  rfidTagId?: string;
  description?: string;
  categoryId?: string;
  currentShelfId?: string;
  currentPositionCell?: string;
  branchId: string;
};

export type UpdateItemPayload = {
  name?: string;
  sku?: string;
  rfidTagId?: string;
  description?: string;
  categoryId?: string;
  currentShelfId?: string;
  currentPositionCell?: string;
  branchId?: string;
};

export interface ListItemsPaginatedParams {
  page: number;
  limit: number;
  branchId?: string;
  categoryId?: string;
  currentShelfId?: string;
  zone?: string;
}

export interface ListItemsPaginatedResult {
  data: Item[];
  total: number;
  page: number;
  limit: number;
}

export async function listItemsPaginated(
  params: ListItemsPaginatedParams,
): Promise<ListItemsPaginatedResult> {
  const { page, limit, branchId, categoryId, currentShelfId, zone } = params;
  const queryParams: Record<string, string | number> = { page, limit };
  if (branchId) queryParams.branchId = branchId;
  if (categoryId) queryParams.categoryId = categoryId;
  if (currentShelfId) queryParams.currentShelfId = currentShelfId;
  if (zone) queryParams.zone = zone;
  const response = await apiClient.get<ApiResponse<ListItemsPaginatedResult>>(
    '/api/items',
    { params: queryParams },
  );
  const payload = response.data.data;
  return {
    data: payload.data,
    total: payload.total,
    page: payload.page,
    limit: payload.limit,
  };
}

export async function listItems(branchId?: string, categoryId?: string): Promise<Item[]> {
  const params: Record<string, string> = {};
  if (branchId) params.branchId = branchId;
  if (categoryId) params.categoryId = categoryId;
  const response = await apiClient.get<ApiResponse<Item[]>>('/api/items', {
    params,
  });
  return response.data.data;
}

export async function getItemById(id: string): Promise<Item> {
  const response = await apiClient.get<ApiResponse<Item>>(`/api/items/${id}`);
  return response.data.data;
}

export async function createItem(payload: CreateItemPayload): Promise<Item> {
  const response = await apiClient.post<ApiResponse<Item>>('/api/items', payload);
  return response.data.data;
}

export type CreateBulkByCategoryPayload = {
  categoryId: string;
  branchId: string;
  count: number;
  currentShelfId?: string;
};

export async function createBulkByCategory(
  payload: CreateBulkByCategoryPayload,
): Promise<Item[]> {
  const response = await apiClient.post<ApiResponse<{ created: Item[] }>>(
    '/api/items/bulk-by-category',
    payload,
  );
  return response.data.data.created;
}

export async function updateItem(
  id: string,
  payload: UpdateItemPayload,
): Promise<Item> {
  const response = await apiClient.put<ApiResponse<Item>>(
    `/api/items/${id}`,
    payload,
  );
  return response.data.data;
}

export async function deleteItem(id: string): Promise<void> {
  await apiClient.delete<ApiResponse<void>>(`/api/items/${id}`);
}
